import React, { useState, useMemo } from "react";
import useSWR from "swr";
import BuildingIcon from "../../../svgs/building.svg";
import TypeIcon from "../../../svgs/type.svg";
import LockIcon from "../../../svgs/shopping-bag.svg";
import FileUpload from "../../Common/FileUpload";
import {
  inventoryFetcher,
  InventoryService,
} from "../../../network/InventoryService";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import FormField from "../../../../Shared/Form/FormField";

const StockAddModal = ({ showAddStockModal, setShowAddStockModal }) => {
  const [form, setForm] = useState({
    warehouse: "",
    inventoryType: "",
    poNumber: "",
    warehouseEntryDate: "",
  });
  const [formError, setFormError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const dispatchNotification = useNotification();

  const warehousesApiUrl = useMemo(() => {
    const params = new URLSearchParams({
      is_dropdown: true,
    });

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-warehouse?${params.toString()}`;
  }, []);

  const { data: warehousesResponse, error: warehousesError } = useSWR(
    warehousesApiUrl,
    inventoryFetcher,
    {
      fallbackData: {},
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const posApiUrl = useMemo(() => {
    if (!form.inventoryType) return null;

    const params = new URLSearchParams({
      is_dropdown: true,
    });

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-po?${params.toString()}&type=${
      form.inventoryType
    }`;
  }, [form.inventoryType]);

  const { data: posResponse, error: posError } = useSWR(
    posApiUrl,
    inventoryFetcher,
    {
      fallbackData: {},
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  );

  const warehouses = warehousesResponse?.data || [];
  const pos = posResponse?.data || [];
  const warehousesLoading = !warehousesResponse && !warehousesError;
  const posLoading = !posResponse && !posError;

  const warehouseOptions = [
    {
      value: "",
      label: warehousesLoading
        ? "Loading warehouses..."
        : warehousesError
          ? "Error loading warehouses"
          : "Select warehouse...",
      isPlaceholder: true,
    },
    ...warehouses.map((warehouse) => ({
      value: warehouse.unique_id || warehouse.id.toString(),
      label: `${warehouse.name} - ${warehouse.address}`,
    })),
  ];

  const inventoryTypeOptions = [
    {
      value: "",
      label: "Inventory type Device/Sim/Other...",
      isPlaceholder: true,
    },
    { value: "device", label: "Device" },
    { value: "sim", label: "Sim" },
    { value: "other", label: "Other" },
  ];

  const poNumberOptions = [
    {
      value: "",
      label: posLoading
        ? "Loading POs..."
        : posError
          ? "Error loading POs"
          : "Select PO Number...",
      isPlaceholder: true,
    },
    ...pos.map((po) => ({
      value: po.po_number,
      label: po.po_number,
    })),
  ];

  const uploadHints = [
    "Device ID",
    "Device Type",
    "Device Serial Number",
    "Device IMEI Number",
    "Warranty Start Date",
    "Warranty End Date",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "text/csv",
      ];

      if (allowedTypes.includes(file.type) || file.name.endsWith(".csv")) {
        setUploadedFile(file);
      } else {
        dispatchNotification({
          type: "ERROR",
          message: "Please upload only Excel (.xlsx, .xls) or CSV files.",
        });
      }
    }
    event.target.value = "";
  };

  const handleDownloadSample = async () => {
    try {
      if (!form.inventoryType) {
        dispatchNotification({
          type: "ERROR",
          message: "Please select an inventory type.",
        });
        return;
      }

      setIsDownloading(true);
      const endpoint = `${process.env.REACT_APP_API_URL}/api/payment_speaker/upload/new-stock?inventory_type=${form.inventoryType}`;
      await InventoryService.downloadSampleFile(
        endpoint,
        "stock-upload-sample.xlsx"
      );

      dispatchNotification({
        type: "SUCCESS",
        message: "Sample file downloaded successfully!",
      });
    } catch (error) {
      console.error("Error downloading sample file:", error);
      dispatchNotification({
        type: "ERROR",
        message: "Failed to download sample file. Please try again.",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.warehouse ||
      !form.inventoryType ||
      !form.poNumber ||
      !form.warehouseEntryDate
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    if (!uploadedFile) {
      setFormError("Please upload a stock file.");
      return;
    }

    setIsUploading(true);
    setFormError("");

    try {
      const stockData = {
        warehouse_unique_id: form.warehouse,
        inventory_type: form.inventoryType,
        po_number: form.poNumber,
        warehouse_entry_date: form.warehouseEntryDate,
      };

      const response = await InventoryService.uploadNewStock(
        stockData,
        uploadedFile
      );

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Stock uploaded successfully!",
        });

        setForm({
          warehouse: "",
          inventoryType: "",
          poNumber: "",
          warehouseEntryDate: "",
        });
        setUploadedFile(null);
        setShowAddStockModal(false);
      } else {
        throw new Error(response.message || "Failed to upload stock");
      }
    } catch (error) {
      console.error("Error uploading stock:", error);

      let errorMessage = "Failed to upload stock. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);
      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="inventory-modal-overlay"
      onClick={() =>
        !isUploading && !isDownloading && setShowAddStockModal(false)
      }
    >
      <div
        className="inventory-modal stock-add-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inventory-modal-header">
          <h3>Inventory Lot Upload Form</h3>
          <button
            className="close-btn"
            type="button"
            onClick={() =>
              !isUploading && !isDownloading && setShowAddStockModal(false)
            }
            disabled={isUploading || isDownloading}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="inventory-modal-body">
            <div className="inventory-modal-body-content">
              <div className="inventory-form-container">
                <FormField
                  id="warehouse"
                  name="warehouse"
                  title="Warehouse"
                  type="select"
                  icon={BuildingIcon}
                  options={warehouseOptions}
                  value={form.warehouse}
                  onChange={handleInputChange}
                  disabled={isUploading || warehousesLoading || warehousesError}
                  isFlat
                  required
                />

                <FormField
                  id="inventoryType"
                  name="inventoryType"
                  title="Inventory Type"
                  type="select"
                  icon={TypeIcon}
                  options={inventoryTypeOptions}
                  value={form.inventoryType}
                  onChange={handleInputChange}
                  disabled={isUploading}
                  isFlat
                  required
                />

                <FormField
                  id="poNumber"
                  name="poNumber"
                  title="PO Number"
                  type="select"
                  icon={LockIcon}
                  options={poNumberOptions}
                  value={form.poNumber}
                  onChange={handleInputChange}
                  disabled={isUploading || posLoading || posError}
                  isFlat
                  required
                />

                <FormField
                  id="warehouseEntryDate"
                  name="warehouseEntryDate"
                  title="Warehouse Entry Date"
                  type="date"
                  value={form.warehouseEntryDate}
                  onChange={handleInputChange}
                  disabled={isUploading}
                  isFlat
                  required
                />
              </div>

              <div className="stock-upload-section">
                <FileUpload
                  uploadedFile={uploadedFile}
                  setUploadedFile={setUploadedFile}
                  handleFileUpload={handleFileUpload}
                  handleFileChange={setUploadedFile}
                  handleDownloadSample={handleDownloadSample}
                  hints={uploadHints}
                  isUploading={isUploading}
                  isDownloading={isDownloading}
                  isDisabled={isUploading || isDownloading}
                />
              </div>
            </div>
          </div>

          {formError && <div className="inventory-form-error">{formError}</div>}

          <div className="inventory-modal-footer">
            <button
              className="cancel-btn"
              type="button"
              onClick={() =>
                !isUploading && !isDownloading && setShowAddStockModal(false)
              }
              disabled={isUploading || isDownloading}
            >
              Cancel
            </button>
            <button
              className="submit-btn"
              type="submit"
              disabled={
                isUploading ||
                isDownloading ||
                !form.warehouse ||
                !form.inventoryType ||
                !form.poNumber ||
                !form.warehouseEntryDate ||
                !uploadedFile
              }
            >
              {isUploading ? "Uploading..." : "Upload Stock"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAddModal;
